/**
 * HelpInfo
 *
 * Reusable "how does this work?" icon + collapsible instructions
 * panel. Drop into any CardHeader so a first-time user can learn
 * the panel's purpose and steps without leaving the page.
 *
 *   <CardTitle className="flex items-center gap-2">
 *     My Pipeline
 *     <HelpInfo
 *       title="How to use My Pipeline"
 *       steps={[
 *         'Use the tabs to filter by stage.',
 *         'Click a deal to open its detail page.',
 *         <>The <em>Stale</em> badge means the deal hasn't moved in 7+ days.</>,
 *       ]}
 *       tip="Stale deals are the first to renegotiate or drop."
 *     />
 *   </CardTitle>
 *
 * Closed by default — the icon is a quiet hint in the header. The
 * panel toggles open/closed on click and renders directly below the
 * card header in the same width as the card.
 *
 * Design choices:
 * - One state per instance (no global help system) — each card
 *   manages its own open/closed.
 * - Steps as an array makes additions easy without juggling JSX.
 * - The icon turns violet when the panel is open so the toggle
 *   state is unambiguous.
 */

import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

export default function HelpInfo({
  title = 'How this works',
  steps = [],
  tip,
  // Where the icon renders. 'inline' = right next to the title;
  // 'end' = ml-auto pushed to the far right of the title row.
  align = 'end',
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }}
        aria-label={title}
        title={title}
        className={`${align === 'end' ? 'ml-auto' : ''} inline-flex items-center justify-center w-6 h-6 rounded-full transition flex-shrink-0 ${
          open
            ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-slate-800'
        }`}
        data-help-toggle
      >
        <Info size={14} />
      </button>
      {open && (
        // The panel mounts as a sibling of the trigger inside the
        // CardTitle, then absolutely-positions inside its own
        // parent-relative wrapper so it spans the card width. We
        // use a static positioned div with a small top margin
        // instead — that way it lays out naturally below the title
        // and won't overlap surrounding content.
        <HelpPanel title={title} steps={steps} tip={tip} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

// Split out so the position/style stays focused and easy to tweak.
function HelpPanel({ title, steps, tip, onClose }) {
  return (
    <div
      className="basis-full mt-3 rounded-lg border bg-violet-50 border-violet-200 text-slate-700 text-sm p-4 dark:bg-violet-900/10 dark:border-violet-700/40 dark:text-slate-200"
      // basis-full forces the panel onto its own line inside the
      // flex CardTitle — without it, panels rendered next to the
      // text would squeeze the title and overflow.
    >
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold flex items-center gap-1.5">
          <Info size={14} /> {title}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close instructions"
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          <X size={14} />
        </button>
      </div>
      {steps.length > 0 && (
        <ol className="list-decimal pl-5 mt-2 space-y-1.5 font-normal">
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      )}
      {tip && (
        <p className="text-xs mt-2 text-slate-500 dark:text-slate-400">
          <em>Tip:</em> {tip}
        </p>
      )}
    </div>
  );
}
